import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import GameDayPage, { buildGameDaySchemas, Section, SITE, type Faq } from "@/components/GameDayPage";
import heroImage from "@/assets/sprinter-limo-party.jpg?format=webp&quality=72&w=1400&imagetools";
import limoImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1000&imagetools";
import execImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1000&imagetools";
import jetImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1000&imagetools";

import suvCaptainImage from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1000&imagetools";
import suvImage from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1000&imagetools";

const PAGE_URL = `${SITE}/uga-game-day-transportation`;
const TITLE = "Atlanta to Athens Party Bus for UGA Game Day | Sprinter Limo";
const DESC =
  "Party bus from Atlanta to Athens in a luxury Sprinter limo: bar, 2 TVs, premium sound, BYOB. Dropped off and picked up at the same spot. Check your game date by text.";

const areas = ["Buckhead", "Midtown", "Downtown", "Sandy Springs", "Alpharetta", "Roswell", "Johns Creek", "Dunwoody", "Marietta", "Decatur", "Athens hotels"];

const faqs: Faq[] = [
  { q: "Is a Sprinter limo the same as a party bus?", a: "It's the party bus experience, with a bar, TVs, lights and sound, in a newer, cleaner and more comfortable luxury Sprinter with a professional chauffeur instead of a bus driver." },
  { q: "Can you stand up inside?", a: "Yes, every one of our Sprinter limos has stand-up headroom so your group can move around on the drive to Athens." },
  { q: "Can we play our own music?", a: "Yes. Connect by Bluetooth or Apple CarPlay and play your own playlist on the premium sound system." },
  { q: "How far in advance should we book a UGA game day party bus?", a: "As early as you can. Our custom fleet is small, and big games like Auburn and Georgia Tech go first. Once a date is gone, it's gone." },
  { q: "How long does an Atlanta to Athens game day take?", a: "Athens is about 70 miles from Atlanta, roughly 1.5 hours each way. With tailgating and the game, it is usually an 8 to 12 hour day depending on your preference, and additional hours can always be added." },
  { q: "Where do you drop off near Sanford Stadium?", a: "As close to campus and Sanford Stadium as game day road closures allow. Your pickup after the game is the same spot where you were dropped off, so you always know where to find your chauffeur." },
  { q: "Does the chauffeur wait during the game?", a: "Yes. Your chauffeur stays staged nearby for the whole game, so your ride is ready after the final whistle." },
  { q: "Can we bring our own drinks and coolers?", a: "Guests 21+ are welcome to bring their own drinks for the ride. Every Sprinter has its own built-in cooler, so there is no need to bring one. If you book a Suburban SUV, bring your cooler along. We always provide ice and water." },
  { q: "Can we bring alcohol?", a: "Yes, it is BYOB. Guests 21+ may bring their own drinks, and the bar is set with glassware and ice. We do not provide or sell alcohol." },
  { q: "How many people fit?", a: "The Limo Style Sprinter seats up to 14, the Executive Sprinter up to 13 and the Jet Style Sprinter up to 7. Our Suburbans seat 6 with captain seats or 7 with bench seats. Larger groups can book more than one vehicle." },
  { q: "What's the deposit?", a: "A 50% deposit secures your date. Sprinters start at $250/hour, custom quotes are given upon request, and your final quote is always in writing before you book." },
  hourlyMinimumFaq(),
];

export const Route = createFileRoute("/uga-game-day-transportation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: buildGameDaySchemas({
      url: PAGE_URL,
      name: "UGA Game Day Party Bus & Sprinter Limo from Atlanta to Athens",
      description: DESC,
      serviceType: "Game day party bus and Sprinter limo transportation",
      areas: ["Atlanta", "Athens"],
      faqs,
      crumb: "UGA Game Day Transportation",
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <GameDayPage
      eyebrow="Atlanta to Athens · Game Day"
      h1="UGA Game Day Party Bus & Sprinter Limo from Atlanta to Athens"
      intro="You've planned a full day in Athens. My chauffeur handles the drive to Sanford Stadium and waits through the game, while you enjoy your group, your playlist and the ride."
      heroImg={heroImage}
      heroAlt="Sprinter limo party bus for UGA game day, Atlanta to Athens"
      champagneText="BYOB: guests 21+ may bring their own drinks. The bar is set with glassware and ice. We do not provide or sell alcohol."
      smallPrint="BYOB for guests 21+. We do not provide or sell alcohol. Features vary by vehicle; ask us which Sprinter fits your group."
      bigDates={{ heading: "2026 UGA Home Games We're Booking", events: ["UGA vs. Missouri", "UGA vs. Georgia Tech"] }}
      steps={{
        title: "How Your Game Day Works",
        items: [
          { t: "Pickup at your home or hotel", d: "Your chauffeur arrives early anywhere in metro Atlanta with ice and water ready. Bringing a Suburban? He loads your cooler and chairs." },
          { t: "Bar, music and TVs on the drive to Athens", d: "Pre-game on the TVs, your playlist on the speakers. The tailgate starts in your driveway." },
          { t: "Drop-off near campus and Sanford Stadium", d: "Where your chauffeur drops you off is exactly where he picks you up after the game, so you always know where to find him." },
          { t: "Tailgate", d: "The Sprinter is the tailgate party: built-in TVs, a bar and a premium sound system, all in one." },
          { t: "Your chauffeur waits during the game", d: "When the game ends, text your chauffeur. He gets the AC on and has everything ready to get you home safely." },
          { t: "Ride home together", d: "The ride back to Atlanta is the afterparty, with more fun all the way to your door." },
        ],
        note: "A UGA game day from Atlanta to Athens is usually an 8 to 12 hour day, depending on your preference. Time can always be extended and additional hours can be added.",
      }}
      middle={
        <Section
          title="Atlanta to Athens Transportation, Without the Hassle"
          intro={
            <>
              <p>A party bus to a UGA game sounds great until you're packed onto a tired bus with strangers' spilled drinks and a driver who drops you at the edge of town. A Sprinter party bus is different: it's your group only, in a newer and cleaner vehicle, with a chauffeur who knows Athens on game day.</p>
              <p>Nobody has to be the designated driver, nobody pays for parking near Sanford Stadium, and nobody has to figure out a ride home when half of Athens is trying to leave at once. If you've been searching for a party bus rental in Atlanta for game day, this is the upgrade.</p>
            </>
          }
        />
      }
      vehicles={{
        title: "Pick Your Ride",
        intro: "Three custom Sprinters, each one of one. Tell us your group size and we'll match you to the right one.",
        list: [
          { name: "Limo Style Sprinter", cap: "Up to 14 guests", detail: "The party bus experience, with bar, 2 TVs, LED ceiling and premium sound.", img: limoImage, alt: "Limo style Sprinter party bus interior with lounge seating, bar and TV" },
          { name: "Executive Sprinter", cap: "Up to 13 guests", detail: "Premium leather and a more relaxed ride to Athens.", img: execImage, alt: "Executive Sprinter limo interior with leather seating for a UGA game day group" },
          { name: "Jet Style Sprinter", cap: "Up to 7 guests", detail: "First-class captain seats, starlight ceiling and a cinema screen.", img: jetImage, alt: "Jet style Sprinter with white leather captain seats and white interior" },
          { name: "Luxury Suburban SUV, Captain Seats", cap: "Up to 6 guests", detail: "Leather captain's chairs for a smaller, private ride.", img: suvCaptainImage, alt: "Luxury Suburban SUV with leather captain seats for game day in Atlanta" },
          { name: "Suburban SUV, Bench Seats", cap: "Up to 7 guests", detail: "Bench seating for a group of up to 7.", img: suvImage, alt: "Black Suburban SUV with bench seating for game day in Atlanta" },
        ],
        note: "The bar, TVs, sound system, LED lighting, stand-up headroom and CarPlay are on every Sprinter. Our Suburbans are a quieter, private ride for smaller groups.",
      }}
      afterVehicles={
        <>
          <Section title="Game Day Pickups Across Metro Atlanta" intro={<p>Homes, hotels and offices, including multiple stops for your crew.</p>}>
            <ul className="flex flex-wrap justify-center gap-2">
              {areas.map((a) => <li key={a} className="rounded-full border border-border bg-card px-4 py-1.5 text-sm">{a}</li>)}
            </ul>
          </Section>
          <Section title="Split It With Your Crew" alt>
            <p className="text-center text-foreground max-w-3xl mx-auto leading-relaxed">
              Sprinters from $250/hour · Custom quotes given upon request · 50% deposit secures your date. Split between your group, it's often less per person than you'd expect. Your exact quote is always in writing before you book.
            </p>
          </Section>
        </>
      }
      faqTitle="UGA Game Day Party Bus FAQ"
      faqs={faqs}
      occasion="Game Day"
      bookTitle="Check Your Game Date"
    />
  );
}
